import { Step } from 'react-joyride';

interface StepProps {
  hed: string;
  dek: string;
  secondDek?: string;
  thirdDek?: string;
}

export interface StepComponent extends Step {
  nextButtonText?: string;
  endTourText?: string;
}

export const StepContent = ({ hed, dek, secondDek, thirdDek }: StepProps) => {
  return (
    <>
      <h3>{hed}</h3>
      <p>{dek}</p>
      <p>{secondDek}</p>
      <p>{thirdDek}</p>
    </>
  );
};

/**
 * Split-screen tour for a user with an unpublished storefront
 */

export const SPLIT_SCREEN_STEP_EDIT: StepComponent = {
  target: '#edit-pane',
  content: (
    <StepContent
      hed="Listing sections"
      dek="Complete your listing by clicking into and filling out each of these required sections."
      secondDek="The more details you share, the better we can connect you with the right couples.
      "
    />
  ),
  placement: 'right-start',
  spotlightPadding: 0,
  disableBeacon: true,
};

export const SPLIT_SCREEN_STEP_PREVIEW: StepComponent = {
  target: '#preview-pane',
  content: (
    <StepContent
      hed="Listing preview"
      dek="When you update and save a section, the changes will appear here in your listing preview. This is what couples will see once your listing is published."
    />
  ),
  placement: 'left-start',
  spotlightPadding: 0,
  disableBeacon: true,
};

export const SPLIT_SCREEN_STEP_PUBLISH: StepComponent = {
  target: '#edit-header-container',
  content: (
    <StepContent
      hed="Request to publish"
      dek="When you’ve completed the required sections, you can request to publish."
      secondDek="Once approved, your listing will be live for couples to discover!"
    />
  ),
  placement: 'right-start',
  spotlightPadding: 0,
  disableBeacon: true,
};

/**
 * Nav tour for the new dashboard, inbox and resources
 */

export const NAV_STEP_DASHBOARD: StepComponent = {
  target: '#dashboard-nav-item',
  content: (
    <StepContent
      hed="Dashboard"
      dek="Manage your account, see a snapshot of your listing's performance, and get tips on how to improve it."
    />
  ),
  disableBeacon: true,
};

export const NAV_STEP_LEADS: StepComponent = {
  target: '#leads-nav-item',
  content: (
    <StepContent
      hed="Leads (formerly Inbox)"
      dek="This is your home for managing leads. See new inquiries, discover prospects (formerly Find Couples) and easily manage your connections."
    />
  ),
  disableBeacon: true,
};

export const NAV_STEP_RESOURCES: StepComponent = {
  target: '#resources-nav-item',
  content: (
    <StepContent
      hed="Resources"
      dek="A brand new hub for tutorials, FAQ's, tips and trends to help your business grow."
    />
  ),
  disableBeacon: true,
};

/**
 * Leads tour for the inbox, conversations and lead details
 */

export const LEADS_STEP_INBOX: StepComponent = {
  target: '#leads-panel',
  content: (
    <StepContent
      hed="Stay organized "
      dek="All your leads and conversations are centralized here."
    />
  ),
  placement: 'right-start',
  spotlightPadding: 0,
  disableBeacon: true,
};

export const LEADS_STEP_INBOX_TAB_PROSPECTS: StepComponent = {
  target: '#inbox-tab-prospects',
  content: (
    <StepContent
      hed="Prospects (formerly Find couples)"
      dek="Say hello first! We'll drop potential leads that are a match for you here. Simply reach out to start the conversation."
    />
  ),
  placement: 'right-start',
  spotlightPadding: 0,
  disableBeacon: true,
};

export const LEADS_STEP_INBOX_TAB_INQUIRIES: StepComponent = {
  target: '#inbox-tab-inquiries',
  content: (
    <StepContent
      hed="Inquiries"
      dek="Leads that have sent you an inquiry will show up here. Keep an eye out for Gold leads who have the highest intent to book."
    />
  ),
  placement: 'right-start',
  spotlightPadding: 0,
  disableBeacon: true,
};

// This is a step variant for planners, who don't have gold inquiries
export const LEADS_STEP_INBOX_TAB_INQUIRIES_PLANNERS: StepComponent = {
  target: '#inbox-tab-inquiries',
  content: (
    <StepContent hed="Inquiries" dek="Leads that have sent you an inquiry will show up here." />
  ),
  placement: 'right-start',
  spotlightPadding: 0,
  disableBeacon: true,
};

export const LEADS_STEP_INBOX_TAB_CONVERSATIONS: StepComponent = {
  target: '#inbox-tab-conversations',
  content: (
    <StepContent hed="Conversations" dek="Messages with your connections will show up here." />
  ),
  placement: 'right-start',
  spotlightPadding: 0,
  disableBeacon: true,
};

export const LEADS_STEP_CONVERSATION_PANEL: StepComponent = {
  target: '#conversation-panel',
  content: (
    <StepContent
      hed="Activity feed"
      dek="Manage your connections and conversations from this panel."
    />
  ),
  placement: 'left-start',
  spotlightPadding: 0,
  disableBeacon: true,
};

export const LEADS_STEP_DETAILS_PANEL: StepComponent = {
  target: '#details-panel',
  content: <StepContent hed="Lead details" dek="Contact info and wedding details for each lead." />,
  placement: 'left-start',
  spotlightPadding: 0,
  disableBeacon: true,
};
